import { SubmissionErrorBoundary } from '@/components/errors/SubmissionErrorBoundary';
import { TsForm } from '@/components/form/Form';
import { SelectInputField, SelectOption } from '@/components/form/SelectInput';
import { SubmitCancelButtons } from '@/components/form/SubmitCancelButtons';
import { TextField } from '@/components/form/TextInput';
import { Modal } from '@/components/shared/modal/Modal';
import { handleMutationResult } from '@/helpers/toastHelper';
import { useContracts } from '@/services/contract/useContracts';
import { useCreateContractMutation } from '@/services/gql/graphql.generated';
import { strings } from '@/services/translation/strings';
import { FunctionComponent, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import adminStrings from '../admin.translations';

interface AddContractFormValues {
  name: string;
  baseContractId?: string;
}

interface AdminAddContractModalProps {
  open: boolean;
  onClose: () => void;
}

const AdminAddContractModal: FunctionComponent<AdminAddContractModalProps> = ({ open, onClose }) => {
  const navigate = useNavigate();

  const [{ fetching }, createContract] = useCreateContractMutation();

  const methods = useForm<AddContractFormValues>();

  const { contracts } = useContracts();

  const contractOptions: SelectOption[] = useMemo(() => {
    const mappedOptions = contracts.map(c => {
      return {
        name: c.name ?? '',
        value: c.id
      };
    });

    return [{ id: '', name: adminStrings.contractsSection.addDialog.placeholder }, ...mappedOptions];
  }, [contracts]);

  const onSubmit = async (values: AddContractFormValues) => {
    const baseContract = contracts.find(c => c.id === values.baseContractId);

    const { id, name, roles, ...inheritableFields } = baseContract ?? {};

    const result = await createContract({
      contract: {
        ...inheritableFields,
        name: values.name,
        active: true
      }
    });

    await handleMutationResult(result, {
      successMessage: strings.common.success,
      errorMessage: strings.common.error,
      customSuccessCheck: data => !!data?.createContract,
      onSuccess: () => {
        methods.reset();
        if (result.data?.createContract) {
          const newContractId = result.data?.createContract.id;
          navigate(`${newContractId}`, { state: { contract: result.data.createContract } });
        }
      }
    });
  };

  const handleClose = () => {
    methods.reset();
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} header={adminStrings.contractsSection.addDialog.title}>
      <SubmissionErrorBoundary>
        <TsForm methods={methods} onSubmit={onSubmit}>
          <div className="flex flex-col space-y-3 w-full">
            <TextField
              label={adminStrings.contractsSection.addDialog.newContract}
              name="name"
              id="name"
              placeholder={adminStrings.contractsSection.addDialog.contractName}
              customValidatorMessage={methods.formState.errors.name?.message}
              required
              rules={{
                required: adminStrings.contractsSection.addDialog.nameRequired,
                validate: (value: string) => {
                  const isDuplicate = contracts?.some(s => s?.name === value);
                  if (isDuplicate) return adminStrings.contractsSection.addDialog.uniqueName;
                  return true;
                }
              }}
            />

            <SelectInputField
              options={contractOptions}
              id={'baseContractId'}
              name={'baseContractId'}
              label={adminStrings.contractsSection.addDialog.baseContract}
              description={adminStrings.contractsSection.addDialog.baseContractDescription}
              optional
            />

            <div className="flex items-center justify-end gap-3">
              <SubmitCancelButtons
                disabled={!!methods.formState.errors.name?.message}
                onComplete={handleClose}
                loading={fetching}
                submitText={strings.common.create}
              />
            </div>
          </div>
        </TsForm>
      </SubmissionErrorBoundary>
    </Modal>
  );
};

export default AdminAddContractModal;
